/**
 * AgroTech — Survey Mission Planner  (Step 5 of the 9-step intelligence loop)
 *
 * Loads all anomalies with status = "investigating" (confirmed in Step 4).
 * For the selected anomaly, derives a deterministic AI survey plan from the
 * anomaly type, severity, and area, auto-generates grid coverage waypoints, then:
 *   • Calls /api/analysis/mission-briefing  → AI contextual rationale + pilot brief
 *   • Calls /api/analysis/weather-check     → Open-Meteo forecast + AI go/no-go
 *   • Provides PDF/Printable mission briefing export
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Navigation, Layers, Wind, Clock, Cpu, CheckCircle2, Edit3, ChevronDown, ChevronUp, RotateCcw,
  AlertTriangle, ChevronRight, Loader2, Info, CloudRain,
  ThumbsUp, ThumbsDown, CloudLightning, Thermometer, Eye,
  MapPin, FileText, Download, Check, ShieldCheck
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { anomalyApi, missionsApi, analysisApi, agrotechDroneApi } from '../../services/api';
import api from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Anomaly {
  id: string;
  boundary_name: string;
  boundary_id: string;
  anomaly_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: string;
  change_percentage: number | string;
  affected_area_hectares?: number | string;
  description?: string;
  detection_date: string;
  resolution_notes?: string;
  boundary_geojson?: { type: string; coordinates: number[][][] };
}

interface ApprovedAsset {
  id: string;
  manufacturer: string;
  model: string;
  asset_type: string;
  status: string;
  specifications?: { max_flight_time_min?: number; max_speed_ms?: number; payload_capacity_kg?: number };
}

interface Waypoint {
  lat: number;
  lng: number;
  alt: number;
  sequence: number;
}

// ─── Deterministic survey-plan derivation ─────────────────────────────────────

interface SurveyPlan {
  primaryPayload: string;
  secondaryPayload: string | null;
  recommendedAltitude: number;
  overlapPercent: number;
  estimatedFlightMin: number;
  gridPattern: string;
  weatherConstraints: string[];
  safetyNotes: string[];
}

function deriveplan(anomaly: Anomaly): SurveyPlan {
  const area = Number(anomaly.affected_area_hectares ?? 1);
  const type = (anomaly.anomaly_type ?? '').toLowerCase();
  const sev  = anomaly.severity;

  const densityFactor = sev === 'critical' || sev === 'high' ? 5 : 4;
  const estimatedFlightMin = Math.max(8, Math.round(area * densityFactor));

  if (type.includes('water') || type.includes('flood') || type.includes('wet')) {
    return {
      primaryPayload:      'Multispectral (NDWI / NDRE)',
      secondaryPayload:    'Thermal (soil moisture mapping)',
      recommendedAltitude: 80,
      overlapPercent:      75,
      estimatedFlightMin,
      gridPattern:         'Double-grid (0° + 90°)',
      weatherConstraints:  ['Wind < 8 m/s', 'No precipitation', 'Solar elevation > 30°'],
      safetyNotes:         ['Avoid flying over standing water at low altitude', 'Check drainage channel locations before flight'],
    };
  }
  if (type.includes('pest') || type.includes('disease') || type.includes('fungal')) {
    return {
      primaryPayload:      'Multispectral (NDRE / NDVI)',
      secondaryPayload:    'RGB high-res (visual confirmation)',
      recommendedAltitude: sev === 'critical' ? 50 : 70,
      overlapPercent:      80,
      estimatedFlightMin,
      gridPattern:         'Double-grid (0° + 90°)',
      weatherConstraints:  ['Wind < 6 m/s', 'Overcast preferred (diffuse light reduces hot-spots)', 'Temperature < 35 °C'],
      safetyNotes:         ['Maintain 30 m clearance from field workers', 'Log pilot and battery serial numbers pre-flight'],
    };
  }
  if (type.includes('heat') || type.includes('thermal') || type.includes('drought')) {
    return {
      primaryPayload:      'Thermal (LST mapping)',
      secondaryPayload:    'Multispectral (NDVI)',
      recommendedAltitude: 90,
      overlapPercent:      70,
      estimatedFlightMin,
      gridPattern:         'Single-grid (0°)',
      weatherConstraints:  ['Wind < 10 m/s', 'Fly within 2 h of solar noon for peak thermal contrast', 'No precipitation preceding 48 h'],
      safetyNotes:         ['Calibrate thermal sensor with blackbody reference before takeoff', 'Avoid shadows from hedgerows during thermal capture'],
    };
  }
  return {
    primaryPayload:      'Multispectral (NDVI / NDRE / NDWI)',
    secondaryPayload:    sev === 'critical' || sev === 'high' ? 'RGB high-res' : null,
    recommendedAltitude: 75,
    overlapPercent:      75,
    estimatedFlightMin,
    gridPattern:         'Double-grid (0° + 90°)',
    weatherConstraints:  ['Wind < 8 m/s', 'No precipitation', 'Solar elevation > 25°'],
    safetyNotes:         ['Confirm GSD ≤ 5 cm/px at selected altitude', 'File airspace notification if area > 50 ha'],
  };
}

// ─── AI briefing types ────────────────────────────────────────────────────────

interface AiBriefing {
  rationale: string;
  pilotBriefing: string;
  droneRecommendation: string;
}

interface WeatherCheck {
  verdict: 'go' | 'caution' | 'no-go';
  summary: string;
  recommendation: string;
  forecast: {
    date: string;
    maxWindSpeed_ms: number;
    precipitationMm: number;
    cloudCoverPct: number;
    tempMax_c: number;
    tempMin_c: number;
    uvIndex: number;
  };
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function SevBadge({ sev }: { sev: string }) {
  const cls: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 font-bold',
    high:     'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    medium:   'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    low:      'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${cls[sev] ?? cls.low}`}>
      {sev}
    </span>
  );
}

function PlanRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-44 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}

const VERDICT_STYLE: Record<WeatherCheck['verdict'], { bar: string; icon: React.ReactNode; label: string }> = {
  'go':      { bar: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',  icon: <ThumbsUp  className="w-4 h-4 text-green-600 dark:text-green-400" />, label: 'GO' },
  'caution': { bar: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',  icon: <CloudLightning className="w-4 h-4 text-amber-600 dark:text-amber-400" />, label: 'CAUTION' },
  'no-go':   { bar: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',          icon: <ThumbsDown className="w-4 h-4 text-red-600 dark:text-red-400" />, label: 'NO-GO' },
};

// ─── Main Component ───────────────────────────────────────────────────────────

// Plan fields the operator can override
type PlanOverrides = {
  primaryPayload:      string;
  secondaryPayload:    string;
  recommendedAltitude: number;
  overlapPercent:      number;
  estimatedFlightMin:  number;
  gridPattern:         string;
};

export const SurveyMissionPlannerPage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [anomalies,       setAnomalies]       = useState<Anomaly[]>([]);
  const [drones,          setDrones]          = useState<ApprovedAsset[]>([]);
  const [selected,        setSelected]        = useState<Anomaly | null>(null);
  const [selectedDroneId, setSelectedDroneId] = useState('');
  const [missionName,     setMissionName]     = useState('');
  const [flightDate,      setFlightDate]      = useState('');
  const [pilotNotes,      setPilotNotes]      = useState('');
  const [loading,         setLoading]         = useState(true);
  const [creating,        setCreating]        = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [success,         setSuccess]         = useState<string | null>(null);
  const [createdMissionId, setCreatedMissionId] = useState<string | null>(null);
  const [missionCounts,   setMissionCounts]   = useState<Record<string, number>>({});
  const [waypoints,       setWaypoints]       = useState<Waypoint[]>([]);
  const [overrideOpen,    setOverrideOpen]    = useState(false);
  const [overrides,       setOverrides]       = useState<PlanOverrides | null>(null);
  const [overrideDefaults, setOverrideDefaults] = useState<PlanOverrides | null>(null);

  // AI briefing state
  const [aiBriefing,      setAiBriefing]      = useState<AiBriefing | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  // Weather state
  const [weather,         setWeather]         = useState<WeatherCheck | null>(null);
  const [weatherLoading,  setWeatherLoading]  = useState(false);
  const [weatherError,    setWeatherError]    = useState<string | null>(null);

  // ── Load confirmed anomalies + drone fleet + existing mission counts ─────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyRes, droneRes, missionRes] = await Promise.all([
        anomalyApi.getAnomalies({ status: 'investigating' }),
        api.get('/approved-assets', { params: { asset_type: 'drone', status: 'active' } }),
        missionsApi.getAll({ limit: 200 } as any),
      ]);
      const list: Anomaly[] = anomalyRes.anomalies ?? [];
      setAnomalies(list);
      setDrones(droneRes.data?.data?.assets ?? []);

      const allMissions: any[] = missionRes?.data?.missions ?? [];
      const counts: Record<string, number> = {};
      for (const m of allMissions) {
        const aid = m.metadata?.anomaly_id;
        if (aid) counts[aid] = (counts[aid] ?? 0) + 1;
      }
      setMissionCounts(counts);

      if (list.length > 0) setSelected(list[0]);
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.error?.message ?? (typeof d?.error === 'string' ? d.error : null) ?? d?.message ?? e?.message ?? 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Pre-populate mission name, overrides, and generate waypoints when selection changes ──
  useEffect(() => {
    if (selected) {
      setMissionName(`Survey — ${selected.boundary_name} (${selected.anomaly_type.replace(/_/g, ' ')})`);
      const p = deriveplan(selected);
      const d: PlanOverrides = {
        primaryPayload:      p.primaryPayload,
        secondaryPayload:    p.secondaryPayload ?? '',
        recommendedAltitude: p.recommendedAltitude,
        overlapPercent:      p.overlapPercent,
        estimatedFlightMin:  p.estimatedFlightMin,
        gridPattern:         p.gridPattern,
      };
      setOverrides(d);
      setOverrideDefaults(d);
      setOverrideOpen(false);
      
      const ring = selected.boundary_geojson?.coordinates?.[0];
      const plan = deriveplan(selected);
      if (ring && ring.length >= 3) {
        agrotechDroneApi.generateWaypoints({
          coordinates: ring,
          altitudeM: plan.recommendedAltitude,
          overlapPct: plan.overlapPercent,
        }).then(res => {
          if (res?.data?.waypoints) setWaypoints(res.data.waypoints);
        }).catch(() => {
          // fallback client-side waypoints
          setWaypoints([
            { lat: ring[0][1], lng: ring[0][0], alt: plan.recommendedAltitude, sequence: 1 },
            { lat: ring[1][1], lng: ring[1][0], alt: plan.recommendedAltitude, sequence: 2 },
          ]);
        });
      } else {
        setWaypoints([]);
      }
    }
  }, [selected?.id]);

  // ── Fetch AI briefing when anomaly selection changes ────────────────────────
  useEffect(() => {
    if (!selected) { setAiBriefing(null); return; }

    const plan = deriveplan(selected);
    setBriefingLoading(true);
    setAiBriefing(null);

    analysisApi.getMissionBriefing({
      anomalyType:          selected.anomaly_type,
      severity:             selected.severity,
      changePercentage:     Number(selected.change_percentage ?? 0),
      affectedAreaHectares: Number(selected.affected_area_hectares ?? 1),
      boundaryName:         selected.boundary_name,
      detectionDate:        selected.detection_date,
      description:          selected.description,
      plan: {
        primaryPayload:    plan.primaryPayload,
        secondaryPayload:  plan.secondaryPayload,
        altitudeM:         plan.recommendedAltitude,
        overlapPercent:    plan.overlapPercent,
        gridPattern:       plan.gridPattern,
        estimatedFlightMin: plan.estimatedFlightMin,
        weatherConstraints: plan.weatherConstraints,
      },
    })
      .then((res) => setAiBriefing(res.data.briefing))
      .catch(() => {})
      .finally(() => setBriefingLoading(false));
  }, [selected?.id]);

  // ── Fetch weather when flight date changes ──────────────────────────────────
  useEffect(() => {
    if (!selected || !flightDate) { setWeather(null); setWeatherError(null); return; }

    const ring = selected.boundary_geojson?.coordinates?.[0];
    if (!ring?.length) { setWeather(null); return; }

    const lng = ring.reduce((s, p) => s + p[0], 0) / ring.length;
    const lat = ring.reduce((s, p) => s + p[1], 0) / ring.length;

    const plan = deriveplan(selected);
    setWeatherLoading(true);
    setWeather(null);
    setWeatherError(null);

    analysisApi.getWeatherCheck({
      latitude:           lat,
      longitude:          lng,
      flightDate,
      weatherConstraints: plan.weatherConstraints,
    })
      .then((res) => setWeather(res.data.weather))
      .catch((e: any) => {
        const msg = e?.response?.data?.message ?? e?.message ?? 'Weather check failed';
        setWeatherError(msg);
      })
      .finally(() => setWeatherLoading(false));
  }, [selected?.id, flightDate]);

  // ── Override helpers ────────────────────────────────────────────────────────
  const setOvNum = (k: 'recommendedAltitude' | 'overlapPercent' | 'estimatedFlightMin', v: number) =>
    setOverrides((p) => p ? { ...p, [k]: v } : p);
  const setOvStr = (k: 'primaryPayload' | 'secondaryPayload' | 'gridPattern', v: string) =>
    setOverrides((p) => p ? { ...p, [k]: v } : p);

  const overriddenKeys = overrides && overrideDefaults
    ? (Object.keys(overrides) as (keyof PlanOverrides)[]).filter((k) => overrides[k] !== overrideDefaults[k])
    : [];
  const hasOverrides = overriddenKeys.length > 0;

  // ── Create mission ──────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!selected) return;
    setCreating(true); setError(null);
    try {
      const ring = selected.boundary_geojson?.coordinates?.[0];
      let locationWkt: string | undefined;
      if (ring?.length) {
        const lng = ring.reduce((s, p) => s + p[0], 0) / ring.length;
        const lat = ring.reduce((s, p) => s + p[1], 0) / ring.length;
        locationWkt = `POINT(${lng} ${lat})`;
      }

      const aiPlan = deriveplan(selected);
      const effectivePlan = overrides ? { ...aiPlan, ...overrides, secondaryPayload: overrides.secondaryPayload || null } : aiPlan;
      const drone = drones.find((d) => d.id === selectedDroneId);
      const droneName = drone ? `${drone.manufacturer} ${drone.model}` : undefined;

      const res = await missionsApi.create({
        name:         missionName.trim() || `Survey — ${selected.boundary_name} (${selected.anomaly_type})`,
        description:  [
          `Step 5 survey mission generated from anomaly ID ${selected.id}.`,
          `Anomaly: ${selected.anomaly_type} · Severity: ${selected.severity} · NDVI change: ${Number(selected.change_percentage ?? 0).toFixed(1)}%`,
          `Plan: ${effectivePlan.primaryPayload} at ${effectivePlan.recommendedAltitude} m AGL · ${effectivePlan.overlapPercent}% overlap · ${effectivePlan.gridPattern}`,
          hasOverrides ? `Operator overrides (${overriddenKeys.length}): ${overriddenKeys.join(', ')}` : 'AI defaults used.',
          aiBriefing?.pilotBriefing ? `AI Pilot Brief: ${aiBriefing.pilotBriefing}` : '',
          pilotNotes ? `Pilot notes: ${pilotNotes}` : '',
          weather ? `Weather on ${flightDate}: ${weather.verdict.toUpperCase()} — ${weather.summary}` : '',
        ].filter(Boolean).join('\n'),
        ...(locationWkt ? { location: locationWkt } : {}),
        status:       'planned',
        mission_type: 'drone_survey',
        ...(flightDate ? { start_time: new Date(flightDate).toISOString() } : {}),
        ...(droneName ? { drone_serial_number: droneName } : {}),
        metadata: {
          anomaly_id:             selected.id,
          boundary_name:          selected.boundary_name,
          anomaly_type:           selected.anomaly_type,
          severity:               selected.severity,
          affected_area_hectares: Number(selected.affected_area_hectares ?? 0) || undefined,
          payload:                effectivePlan.primaryPayload,
          secondary_payload:      effectivePlan.secondaryPayload ?? undefined,
          altitude_m:             effectivePlan.recommendedAltitude,
          overlap_pct:            effectivePlan.overlapPercent,
          grid_pattern:           effectivePlan.gridPattern,
          est_flight_min:         effectivePlan.estimatedFlightMin,
          operator_overrides:     hasOverrides ? overriddenKeys : [],
          step:                   5,
          weather_verdict:        weather?.verdict ?? undefined,
          weather_checked_date:   flightDate || undefined,
          waypoints_count:        waypoints.length,
        },
      });

      const missionId: string = res?.data?.mission?.id ?? res?.mission?.id ?? res?.id;
      setCreatedMissionId(missionId ?? null);
      setMissionCounts((prev) => ({ ...prev, [selected.id]: (prev[selected.id] ?? 0) + 1 }));
      setSuccess(`Mission created successfully${missionId ? ` — ID ${missionId.slice(0, 8)}…` : ''}`);
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.error?.message ?? (typeof d?.error === 'string' ? d.error : null) ?? d?.message ?? e?.message ?? 'Failed to create mission');
    } finally {
      setCreating(false);
    }
  };

  const handlePrintBriefing = () => {
    window.print();
  };

  const aiPlan = selected ? deriveplan(selected) : null;
  const plan   = aiPlan && overrides ? { ...aiPlan, ...overrides, secondaryPayload: overrides.secondaryPayload || null } : aiPlan;
  const SELECT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Navigation className="w-7 h-7 text-indigo-600" /> Survey Mission Planner
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 5 — deterministic flight plan with auto-grid waypoints, AI briefing, and live weather check
          </p>
        </div>
        {selected && (
          <Button variant="outline" size="sm" onClick={handlePrintBriefing} className="flex items-center gap-1.5">
            <Download className="w-4 h-4" /> Export Mission Briefing PDF
          </Button>
        )}
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── No confirmed anomalies ── */}
      {anomalies.length === 0 && (
        <Card>
          <div className="flex flex-col items-center text-center py-12 max-w-md mx-auto gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-gray-400" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">No confirmed anomalies</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Survey plans are generated when an anomaly is confirmed in Step 4 — Anomaly Alerts.
              </p>
            </div>
            <Button variant="outline" onClick={() => window.location.href = '/agrotech/anomalies'}>
              Go to Anomaly Alerts →
            </Button>
          </div>
        </Card>
      )}

      {anomalies.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Left: anomaly picker ── */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Confirmed Anomalies ({anomalies.length})
            </p>
            {anomalies.map((a) => {
              const existingCount = missionCounts[a.id] ?? 0;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    setSelected(a);
                    setCreatedMissionId(null);
                    setSuccess(null);
                    setFlightDate('');
                    setWeather(null);
                  }}
                  className={[
                    'w-full text-left p-4 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500',
                    selected?.id === a.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/10'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-indigo-300',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">{a.boundary_name}</p>
                    <SevBadge sev={a.severity} />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize mb-1">{a.anomaly_type}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span>NDVI {Number(a.change_percentage ?? 0).toFixed(1)}%</span>
                    {a.affected_area_hectares && (
                      <span>{Number(a.affected_area_hectares).toFixed(1)} {uom.area_unit_label}</span>
                    )}
                  </div>
                  {existingCount > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-green-600 dark:text-green-400 font-medium">
                      <CheckCircle2 className="w-3 h-3" />
                      {existingCount} mission{existingCount > 1 ? 's' : ''} created
                    </div>
                  )}
                  {selected?.id === a.id && existingCount === 0 && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                      <ChevronRight className="w-3 h-3" /> Plan generated below
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Right: plan + AI + weather + waypoints + mission creation ── */}
          <div className="lg:col-span-2 space-y-5">
            {plan && selected && (
              <>
                {/* ── Plan card ── */}
                <Card>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                      <Cpu className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Survey Flight Plan</h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Parameters determined by anomaly type · severity · area
                      </p>
                    </div>
                    {hasOverrides && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-semibold">
                        <Edit3 className="w-3 h-3" /> {overriddenKeys.length} override{overriddenKeys.length > 1 ? 's' : ''}
                      </span>
                    )}
                    {briefingLoading && (
                      <div className="ml-auto flex items-center gap-1.5 text-xs text-indigo-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating AI briefing…
                      </div>
                    )}
                  </div>

                  {/* AI Rationale */}
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 mb-4">
                    <Info className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-indigo-800 dark:text-indigo-300 leading-relaxed">
                      {aiBriefing?.rationale ?? `${selected.anomaly_type} confirmed at ${selected.boundary_name} — ${plan.primaryPayload} survey will delineate the affected zone at sub-field resolution.`}
                    </p>
                  </div>

                  {/* Plan parameters */}
                  {overrides && overrideDefaults && (
                  <div>
                    <PlanRow label="Primary Payload"  value={<span className={overrides.primaryPayload !== overrideDefaults.primaryPayload ? 'text-amber-600 dark:text-amber-400 font-bold' : 'font-medium'}>{plan.primaryPayload}{overrides.primaryPayload !== overrideDefaults.primaryPayload ? ' ✱' : ''}</span>} />
                    <PlanRow label="Secondary Payload" value={<span className={overrides.secondaryPayload !== overrideDefaults.secondaryPayload ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.secondaryPayload || '—'}{overrides.secondaryPayload !== overrideDefaults.secondaryPayload ? ' ✱' : ''}</span>} />
                    <PlanRow label="Altitude AGL"     value={<span className={overrides.recommendedAltitude !== overrideDefaults.recommendedAltitude ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.recommendedAltitude} m{overrides.recommendedAltitude !== overrideDefaults.recommendedAltitude ? ' ✱' : ''}</span>} />
                    <PlanRow label="Overlap"          value={<span className={overrides.overlapPercent !== overrideDefaults.overlapPercent ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.overlapPercent}%{overrides.overlapPercent !== overrideDefaults.overlapPercent ? ' ✱' : ''}</span>} />
                    <PlanRow label="Grid Pattern"     value={<span className={overrides.gridPattern !== overrideDefaults.gridPattern ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.gridPattern}{overrides.gridPattern !== overrideDefaults.gridPattern ? ' ✱' : ''}</span>} />
                    <PlanRow
                      label="Est. Flight Time"
                      value={
                        <span className={`flex items-center gap-1 ${overrides.estimatedFlightMin !== overrideDefaults.estimatedFlightMin ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}`}>
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          {plan.estimatedFlightMin} min{overrides.estimatedFlightMin !== overrideDefaults.estimatedFlightMin ? ' ✱' : ''}
                          {Number(selected.affected_area_hectares ?? 0) > 0 && (
                            <span className="text-gray-400 ml-1">
                              · {Number(selected.affected_area_hectares).toFixed(1)} {uom.area_unit_label}
                            </span>
                          )}
                        </span>
                      }
                    />
                  </div>
                  )}

                  {/* ── Override accordion ── */}
                  {overrides && overrideDefaults && (
                  <>
                  <button
                    type="button"
                    onClick={() => setOverrideOpen((v) => !v)}
                    className="mt-5 w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/50 dark:bg-indigo-950/10 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 transition-colors text-sm font-semibold text-indigo-700 dark:text-indigo-400"
                  >
                    <span className="flex items-center gap-2">
                      <Edit3 className="w-4 h-4" />
                      {overrideOpen ? 'Hide' : 'Override'} Plan Parameters
                      {hasOverrides && !overrideOpen && <span className="font-normal text-indigo-500">({overriddenKeys.length} modified)</span>}
                    </span>
                    {overrideOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {overrideOpen && (
                    <div className="mt-4 space-y-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Amber fields have been modified from AI defaults.</p>
                        {hasOverrides && (
                          <button type="button" onClick={() => setOverrides({ ...overrideDefaults })} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                            <RotateCcw className="w-3 h-3" /> Reset to AI defaults
                          </button>
                        )}
                      </div>

                      {/* Text fields: payload + grid pattern */}
                      {(['primaryPayload', 'secondaryPayload', 'gridPattern'] as const).map((k) => {
                        const changed = overrides[k] !== overrideDefaults[k];
                        const labels: Record<string, string> = { primaryPayload: 'Primary Payload', secondaryPayload: 'Secondary Payload', gridPattern: 'Grid Pattern' };
                        return (
                          <div key={k} className={`p-3 rounded-lg border transition-colors ${changed ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-[11px] font-semibold uppercase tracking-wider ${changed ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>{labels[k]}{changed ? ' · overridden' : ''}</span>
                              <span className="text-[11px] text-gray-400">AI: {overrideDefaults[k] || '—'}</span>
                            </div>
                            <input
                              type="text"
                              value={overrides[k]}
                              onChange={(e) => setOvStr(k, e.target.value)}
                              placeholder={k === 'secondaryPayload' ? 'Leave blank for none' : ''}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        );
                      })}

                      {/* Numeric fields: altitude, overlap, flight time */}
                      {([
                        { key: 'recommendedAltitude' as const, label: 'Altitude AGL',      unit: 'm',   min: 20,  max: 150, step: 5,   hint: `AI: ${overrideDefaults.recommendedAltitude} m` },
                        { key: 'overlapPercent'       as const, label: 'Overlap',           unit: '%',   min: 60,  max: 95,  step: 5,   hint: `AI: ${overrideDefaults.overlapPercent}%` },
                        { key: 'estimatedFlightMin'   as const, label: 'Est. Flight Time',  unit: 'min', min: 5,   max: 240, step: 5,   hint: `AI: ${overrideDefaults.estimatedFlightMin} min` },
                      ]).map(({ key, label, unit, min, max, step, hint }) => {
                        const changed = overrides[key] !== overrideDefaults[key];
                        return (
                          <div key={key} className={`p-3 rounded-lg border transition-colors ${changed ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-[11px] font-semibold uppercase tracking-wider ${changed ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>{label}{changed ? ' · overridden' : ''}</span>
                              <span className="text-[11px] text-gray-400">{hint}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <input type="number" min={min} max={max} step={step} value={overrides[key]}
                                onChange={(e) => setOvNum(key, Number(e.target.value))}
                                className="w-24 px-2 py-1.5 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                              <span className="text-sm text-gray-500 dark:text-gray-400">{unit}</span>
                              <input type="range" min={min} max={max} step={step} value={overrides[key]}
                                onChange={(e) => setOvNum(key, Number(e.target.value))} className="flex-1 accent-indigo-500" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  </>
                  )}

                  {/* Waypoint grid summary */}
                  {waypoints.length > 0 && (
                    <div className="mt-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-indigo-500" /> Auto-Grid Coverage Waypoints ({waypoints.length})
                        </p>
                        <span className="text-xs text-slate-500">Altitude: {plan.recommendedAltitude}m AGL</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        {waypoints.slice(0, 4).map((wp) => (
                          <div key={wp.sequence} className="p-2 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-800">
                            <span className="font-semibold text-indigo-600">WP #{wp.sequence}</span>: {wp.lat.toFixed(4)}, {wp.lng.toFixed(4)}
                          </div>
                        ))}
                      </div>
                      {waypoints.length > 4 && (
                        <p className="text-[11px] text-slate-400 mt-2">
                          + {waypoints.length - 4} additional coverage waypoints calculated across polygon bounds.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Weather constraints + safety notes */}
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                        <Wind className="w-3.5 h-3.5" /> Weather Constraints
                      </p>
                      <ul className="space-y-1">
                        {plan.weatherConstraints.map((c) => (
                          <li key={c} className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />{c}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> Safety Notes
                      </p>
                      <ul className="space-y-1">
                        {plan.safetyNotes.map((n) => (
                          <li key={n} className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />{n}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>

                {/* ── AI Drone Recommendation card ── */}
                {aiBriefing && (
                  <Card>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Cpu className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="space-y-3 flex-1">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                            Drone / Sensor Recommendation
                          </p>
                          <p className="text-sm text-gray-800 dark:text-gray-200">{aiBriefing.droneRecommendation}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                            Pilot Briefing
                          </p>
                          <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{aiBriefing.pilotBriefing}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* ── Mission Creation card ── */}
                <Card>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-indigo-500" /> Create Survey Mission
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Mission Name *
                      </label>
                      <input
                        className={SELECT_CLS}
                        value={missionName}
                        onChange={(e) => setMissionName(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Approved Drone (Hardware Registry)
                      </label>
                      {drones.length === 0 ? (
                        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          No active drones in Approved Asset List — mission will be created without asset assignment.
                        </p>
                      ) : (
                        <select value={selectedDroneId} onChange={(e) => setSelectedDroneId(e.target.value)} className={SELECT_CLS}>
                          <option value="">— Select an approved drone —</option>
                          {drones.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.manufacturer} {d.model}
                              {d.specifications?.max_flight_time_min ? ` · ${d.specifications.max_flight_time_min} min endurance` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Planned Flight Date
                      </label>
                      <input
                        type="date"
                        value={flightDate}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setFlightDate(e.target.value)}
                        className={SELECT_CLS}
                      />
                    </div>

                    {flightDate && selected.boundary_geojson?.coordinates?.[0]?.length && (
                      <div>
                        {weatherLoading && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Checking weather for {flightDate}…
                          </div>
                        )}
                        {weatherError && !weatherLoading && (
                          <div className="flex items-start gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-500">
                            <CloudRain className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            Weather data unavailable — check a local forecast before flying.
                          </div>
                        )}
                        {weather && !weatherLoading && (() => {
                          const vs = VERDICT_STYLE[weather.verdict];
                          return (
                            <div className={`rounded-lg border p-4 space-y-3 ${vs.bar}`}>
                              <div className="flex items-center gap-2">
                                {vs.icon}
                                <span className={`text-sm font-bold tracking-wide ${
                                  weather.verdict === 'go'      ? 'text-green-700 dark:text-green-400' :
                                  weather.verdict === 'caution' ? 'text-amber-700 dark:text-amber-400' :
                                  'text-red-700 dark:text-red-400'
                                }`}>
                                  Flight Weather — {vs.label}
                                </span>
                                <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">{weather.forecast.date}</span>
                              </div>

                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                                  <Wind className="w-3.5 h-3.5 text-gray-400" />
                                  <span>{weather.forecast.maxWindSpeed_ms} m/s wind</span>
                                </div>
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                                  <CloudRain className="w-3.5 h-3.5 text-gray-400" />
                                  <span>{weather.forecast.precipitationMm} mm rain</span>
                                </div>
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                                  <Eye className="w-3.5 h-3.5 text-gray-400" />
                                  <span>{weather.forecast.cloudCoverPct}% cloud</span>
                                </div>
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                                  <Thermometer className="w-3.5 h-3.5 text-gray-400" />
                                  <span>{weather.forecast.tempMin_c}°–{weather.forecast.tempMax_c}°C</span>
                                </div>
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300 col-span-2">
                                  <span className="text-gray-400">UV</span>
                                  <span>{weather.forecast.uvIndex} index</span>
                                </div>
                              </div>

                              <p className="text-xs text-gray-700 dark:text-gray-200">{weather.summary}</p>
                              <p className="text-xs font-medium text-gray-800 dark:text-gray-100 border-t border-current/20 pt-2">
                                {weather.recommendation}
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Pilot Notes (optional)
                      </label>
                      <textarea
                        value={pilotNotes}
                        onChange={(e) => setPilotNotes(e.target.value)}
                        rows={2}
                        placeholder="Waypoint adjustments, no-fly zones, access notes…"
                        className={`${SELECT_CLS} resize-none`}
                      />
                    </div>

                    {/* Mission summary preview */}
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                      <p><span className="font-medium text-gray-700 dark:text-gray-300">Mission: </span>Survey — {selected.boundary_name} ({selected.anomaly_type})</p>
                      <p><span className="font-medium text-gray-700 dark:text-gray-300">Payload: </span>{plan.primaryPayload}{plan.secondaryPayload ? ` + ${plan.secondaryPayload}` : ''}{hasOverrides ? <span className="text-amber-600 dark:text-amber-400"> ✱ {overriddenKeys.length} override{overriddenKeys.length > 1 ? 's' : ''}</span> : ''}</p>
                      <p><span className="font-medium text-gray-700 dark:text-gray-300">Est. duration: </span>{plan.estimatedFlightMin} min</p>
                      {weather && (
                        <p><span className="font-medium text-gray-700 dark:text-gray-300">Weather: </span>
                          <span className={weather.verdict === 'go' ? 'text-green-600' : weather.verdict === 'caution' ? 'text-amber-600' : 'text-red-600'}>
                            {weather.verdict.toUpperCase()}
                          </span>
                          {' '}on {flightDate}
                        </p>
                      )}
                    </div>

                    {/* Create / Open buttons */}
                    {(() => {
                      const existingCount = missionCounts[selected.id] ?? 0;
                      const alreadyCreated = existingCount > 0 && !createdMissionId;

                      if (createdMissionId) {
                        return (
                          <div className="flex items-center gap-3">
                            <Button variant="outline" onClick={() => window.location.href = `/agrotech/flight-analysis`}>
                              Go to Step 6 — Flight Analysis →
                            </Button>
                            <Button variant="ghost" onClick={() => { setCreatedMissionId(null); setSuccess(null); }}>
                              View plan
                            </Button>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-2">
                          {alreadyCreated && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                              <span>
                                <strong>{existingCount} survey mission{existingCount > 1 ? 's' : ''}</strong> already created for this anomaly.
                                Creating another will duplicate the plan.
                              </span>
                            </div>
                          )}
                          {weather?.verdict === 'no-go' && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-800 dark:text-red-300">
                              <ThumbsDown className="w-4 h-4 flex-shrink-0" />
                              <span>Weather forecast shows <strong>NO-GO</strong> conditions for {flightDate}. Consider choosing a different date.</span>
                            </div>
                          )}
                          <Button
                            variant="primary"
                            onClick={handleCreate}
                            disabled={creating || weather?.verdict === 'no-go'}
                            className="w-full"
                          >
                            {creating ? (
                              <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" /> Creating Mission…
                              </span>
                            ) : (
                              `Create & Dispatch Survey Mission${hasOverrides ? ' (with overrides)' : ''}`
                            )}
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyMissionPlannerPage;
// Made with Bob
